import React, { useState } from "react";
import { 
  FiCheckCircle, 
  FiAlertTriangle, 
  FiClock, 
  FiUser, 
  FiArrowRight, 
  FiCornerDownRight,
  FiRepeat,
  FiBox
} from "react-icons/fi";
import type { 
  ProcessMap, 
  ProcessStep, 
  ProcessStepType 
} from "../../../../types/rca.types.js";

export type ProcessMapWorkspaceProps = {
  rcaRecordId?: string;
  problemStatement: string;
  readOnly?: boolean;
  onSave?: (map: ProcessMap) => void;
  onRootCauseIdentified?: (rootCauseText: string, category: string, confidence: number) => void;
};

const INITIAL_STEPS: ProcessStep[] = [
  { id: "step-1", stepNumber: 1, label: "Raw Material Receiving", type: "STORAGE", responsibleRole: "Warehouse / QA", cycleTimeMinutes: 15, valueAdded: "NNVA", isFailurePoint: false, nextStepIds: ["step-2"] },
  { id: "step-2", stepNumber: 2, label: "CNC Cutting & Milling", type: "PROCESS", responsibleRole: "CNC Machinist", cycleTimeMinutes: 45, valueAdded: "VA", isFailurePoint: false, nextStepIds: ["step-3"] },
  { id: "step-3", stepNumber: 3, label: "In-Process Inspection", type: "INSPECTION", responsibleRole: "Quality Inspector", cycleTimeMinutes: 10, valueAdded: "NNVA", isFailurePoint: true, failureDescription: "Failure point: Micrometer calibration drift allows 0.08mm deviation to pass undetected.", nextStepIds: ["step-4", "step-rework"] },
  { id: "step-rework", stepNumber: 4, label: "Rework / Scrap Loop", type: "REWORK", responsibleRole: "Rework Team", cycleTimeMinutes: 30, valueAdded: "NVA", isFailurePoint: false, nextStepIds: ["step-2"] },
  { id: "step-4", stepNumber: 5, label: "Sub-assembly Integration", type: "PROCESS", responsibleRole: "Assembly Operator", cycleTimeMinutes: 60, valueAdded: "VA", isFailurePoint: false, nextStepIds: ["step-5"] },
  { id: "step-5", stepNumber: 6, label: "Final Quality Gate", type: "INSPECTION", responsibleRole: "Lead QA Inspector", cycleTimeMinutes: 20, valueAdded: "NNVA", isFailurePoint: true, failureDescription: "Failure point: Escaped to customer batch LOT-2026-0789.", nextStepIds: ["step-6"] },
  { id: "step-6", stepNumber: 7, label: "Finished Goods Pack & Ship", type: "STORAGE", responsibleRole: "Logistics", cycleTimeMinutes: 25, valueAdded: "NNVA", isFailurePoint: false, nextStepIds: [] },
];

export function ProcessMapWorkspace({
  rcaRecordId = "rca-current",
  problemStatement,
  readOnly = false,
  onSave,
  onRootCauseIdentified,
}: ProcessMapWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<"map" | "steps" | "failures">("map");
  const [steps, setSteps] = useState<ProcessStep[]>(INITIAL_STEPS);
  const [selectedStep, setSelectedStep] = useState<ProcessStep | null>(INITIAL_STEPS[2] || null);

  const failureSteps = steps.filter(s => s.isFailurePoint);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
            PM
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              15. Process Map Workspace
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Flow & Failure Points
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Map the manufacturing sequence to identify control gaps, bottlenecks, and failure manifestation points.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 p-1 rounded-lg text-xs font-medium border border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab("map")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "map" 
                  ? "bg-white text-emerald-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Visual Flow
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("steps")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "steps" 
                  ? "bg-white text-emerald-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Steps Table ({steps.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("failures")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "failures" 
                  ? "bg-white text-emerald-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Failure Points ({failureSteps.length})
            </button>
          </div>
        </div>
      </div>

      {/* Failure Summary Strip */}
      <div className="px-6 py-2.5 bg-emerald-50/50 border-b border-emerald-100 flex flex-wrap items-center justify-between gap-4 text-xs">
        <div className="flex items-center gap-4">
          <span className="font-semibold text-emerald-900">Failure Points Detected:</span>
          <div className="flex items-center gap-2">
            {failureSteps.map(f => (
              <span key={f.id} className="px-2.5 py-0.5 rounded-full bg-rose-600 text-white font-bold text-[11px] flex items-center gap-1 shadow-sm">
                <FiAlertTriangle className="w-3 h-3" /> Step #{f.stepNumber}: {f.label}
              </span>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            onRootCauseIdentified?.("Process Control Gap at In-Process Inspection allowing drift to escape to Assembly", "METHOD", 88);
          }}
          className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-xs shadow-sm transition-colors"
        >
          Confirm Process Failure Point
        </button>
      </div>

      {/* Canvas */}
      <div className="p-6">
        {activeTab === "map" ? (
          <div className="space-y-6">
            {/* Flow Nodes Canvas */}
            <div className="bg-slate-50/70 p-6 rounded-xl border border-slate-200 overflow-x-auto">
              <div className="flex items-center gap-3 min-w-[900px] justify-center py-4">
                {steps.filter(s => s.type !== "REWORK").map((step, idx, arr) => (
                  <React.Fragment key={step.id}>
                    <div 
                      onClick={() => setSelectedStep(step)}
                      className={`p-3.5 rounded-xl border-2 cursor-pointer transition-all w-36 text-center shrink-0 ${
                        selectedStep?.id === step.id 
                          ? "ring-2 ring-emerald-500 shadow-md" 
                          : "hover:border-slate-400"
                      } ${
                        step.isFailurePoint 
                          ? "bg-rose-50 border-rose-300 text-rose-900 shadow-sm" 
                          : "bg-white border-slate-200 text-slate-900"
                      }`}
                    >
                      <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
                        <span>#{step.stepNumber}</span>
                        <span className="font-semibold text-emerald-600">{step.valueAdded}</span>
                      </div>
                      <div className="text-xs font-bold leading-tight">{step.label}</div>
                      <div className="mt-2 text-[10px] text-slate-500 flex items-center justify-center gap-1">
                        <FiClock className="w-3 h-3" /> {step.cycleTimeMinutes}m
                      </div>
                      {step.isFailurePoint && (
                        <div className="mt-1.5 px-1.5 py-0.5 bg-rose-600 text-white font-bold text-[9px] rounded flex items-center justify-center gap-0.5">
                          <FiAlertTriangle className="w-2.5 h-2.5" /> Failure Point
                        </div>
                      )}
                    </div>

                    {idx < arr.length - 1 && (
                      <div className="text-slate-400 flex flex-col items-center">
                        <FiArrowRight className="w-5 h-5 text-slate-400" />
                        {step.id === "step-3" && (
                          <span className="text-[9px] text-rose-500 font-bold -mt-1">Rework loop</span>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Selected Step Properties Drawer */}
            {selectedStep && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                <div>
                  <span className="text-slate-400 block text-[11px]">Selected Step:</span>
                  <span className="font-bold text-slate-900 text-sm">{selectedStep.label}</span>
                  <span className="block text-slate-500 text-[11px] mt-0.5">Type: {selectedStep.type}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px]">Responsible Role:</span>
                  <span className="font-semibold text-slate-800">{selectedStep.responsibleRole}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px]">Cycle Time & Value Added:</span>
                  <span className="font-semibold text-slate-800">{selectedStep.cycleTimeMinutes} mins ({selectedStep.valueAdded})</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px]">Failure Diagnostic:</span>
                  <span className={selectedStep.isFailurePoint ? "text-rose-600 font-semibold" : "text-emerald-600"}>
                    {selectedStep.failureDescription || "Operating within normal parameters."}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Steps Table */
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border border-slate-200 rounded-lg">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">Step #</th>
                  <th className="py-2.5 px-3">Process Name</th>
                  <th className="py-2.5 px-3">Type</th>
                  <th className="py-2.5 px-3">Responsible</th>
                  <th className="py-2.5 px-3">Cycle Time</th>
                  <th className="py-2.5 px-3">Value Add</th>
                  <th className="py-2.5 px-3">Failure Point?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {steps.map(s => (
                  <tr key={s.id} className={s.isFailurePoint ? "bg-rose-50 font-medium" : ""}>
                    <td className="py-2.5 px-3 font-bold">#{s.stepNumber}</td>
                    <td className="py-2.5 px-3 text-slate-900 font-medium">{s.label}</td>
                    <td className="py-2.5 px-3 text-slate-600">{s.type}</td>
                    <td className="py-2.5 px-3">{s.responsibleRole}</td>
                    <td className="py-2.5 px-3">{s.cycleTimeMinutes}m</td>
                    <td className="py-2.5 px-3">{s.valueAdded}</td>
                    <td className="py-2.5 px-3">
                      {s.isFailurePoint ? (
                        <span className="text-rose-600 font-bold flex items-center gap-1">
                          <FiAlertTriangle className="w-3.5 h-3.5" /> YES
                        </span>
                      ) : "No"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
